import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Chip,
    Box,
    Tooltip,
    Avatar,
    Stack,
    IconButton,
    useTheme,
    alpha,
    TablePagination,
    TableSortLabel,
    TextField,
    InputAdornment,
    Checkbox,
    Link,
} from '@mui/material';
import {
    Work as WorkIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    PersonAdd as PersonAddIcon,
    CheckCircle as CheckCircleIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    AccessTime as AccessTimeIcon,
    InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { EasyFlexWorker } from '../../api/workersApi';

type SortField = 'name' | 'status' | 'occupation' | 'placement' | 'business' | 'experience' | 'hours' | 'updatedAt';
type SortDir = 'asc' | 'desc';

interface EasyFlexWorkersTableProps {
    workers: EasyFlexWorker[];
    loading?: boolean;
    currentPage?: number;
    rowsPerPage?: number;
    totalCount?: number;
    onPageChange?: (page: number) => void;
    onRowsPerPageChange?: (rowsPerPage: number) => void;
    sortField?: SortField;
    sortDir?: SortDir;
    onSortChange?: (field: SortField, dir: SortDir) => void;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    onImport?: (worker: EasyFlexWorker) => void;
    /**
     * Already-imported workers keyed by `${regnum}::${organizationName}`.
     * Rows found here show an "Imported" chip + Open-worker action instead of
     * an Import button, so recruiters can't walk into the duplicate dead-end.
     */
    linkedMap?: Map<string, { workerId: string; name: string }>;
    onOpenWorker?: (workerId: string) => void;
    // Bulk-select mode. When true the table renders a leading checkbox column
    // (sticky left of the Import column) and the parent owns the selection
    // state. Row clicks toggle selection instead of navigating to the import
    // wizard so users can rapid-tick.
    bulkSelectMode?: boolean;
    selectedIds?: Set<string>;
    onToggleRow?: (worker: EasyFlexWorker) => void;
    onTogglePage?: (workersOnPage: EasyFlexWorker[], nextChecked: boolean) => void;
    // Gmail-style "select all matching filter" — surfaces a banner under the
    // toolbar when every visible row is ticked but `totalCount > workers.length`.
    onSelectAllMatching?: () => void;
    isSelectingAllMatching?: boolean;
    allMatchingSelected?: boolean;
}

const EasyFlexWorkersTable: React.FC<EasyFlexWorkersTableProps> = ({
    workers,
    loading = false,
    currentPage = 0,
    rowsPerPage = 25,
    totalCount = 0,
    onPageChange,
    onRowsPerPageChange,
    sortField = 'name',
    sortDir = 'asc',
    onSortChange,
    searchValue = '',
    onSearchChange,
    onImport,
    linkedMap,
    onOpenWorker,
    bulkSelectMode = false,
    selectedIds,
    onToggleRow,
    onTogglePage,
    onSelectAllMatching,
    isSelectingAllMatching = false,
    allMatchingSelected = false,
}) => {
    const theme = useTheme();

    const isRowSelected = (w: EasyFlexWorker) => selectedIds?.has(String(w.id)) ?? false;

    // Granular status badge. Active workers show the recruiter-facing
    // availability (green Available / blue Placed); inactive workers show their
    // real EasyFlex status label (Uit dienst / Recent uit dienst / Ingeschreven…)
    // rather than collapsing into one "Inactive" bucket. The description drives
    // the ℹ️ info tooltip so recruiters can learn what each status means.
    // 21357 "Recent uit dienst" is tinted warning — freshly off-boarded, the
    // pool most likely still worth re-engaging.
    const RECENTLY_LEFT_CODE = 21357;
    const statusBadge = (w: EasyFlexWorker): { label: string; color: string; description: string } => {
        switch (w.availability) {
            case 'AVAILABLE':
                return {
                    label: 'Available',
                    color: theme.palette.success.main,
                    description: 'Active in EasyFlex and not currently on an assignment — available to place now.',
                };
            case 'PLACED':
                return {
                    label: 'Placed',
                    color: theme.palette.info.main,
                    description: 'Active and currently on an assignment — already working, not in the available pool.',
                };
            case 'INACTIVE':
            default:
                return {
                    label: w.easyflexStatusLabel || 'Inactive',
                    color: w.easyflexStatusCode === RECENTLY_LEFT_CODE
                        ? theme.palette.warning.main
                        : theme.palette.grey[500],
                    description: w.easyflexStatusDescription || 'Not currently in service in EasyFlex.',
                };
        }
    };

    // Already-imported lookup: key by regnum+org (regnums collide across orgs).
    const importedMatch = (w: EasyFlexWorker): { workerId: string; name: string } | undefined => {
        if (!linkedMap || w.easyflexRegistrationNumber == null) return undefined;
        return linkedMap.get(`${w.easyflexRegistrationNumber}::${w.organizationName ?? ''}`);
    };
    const pageSelectedCount = workers.filter(isRowSelected).length;
    const allOnPageSelected = workers.length > 0 && pageSelectedCount === workers.length;
    const someOnPageSelected = pageSelectedCount > 0 && pageSelectedCount < workers.length;
    const moreMatchingThanLoaded = totalCount > workers.length;
    const showSelectAllMatchingBanner =
        bulkSelectMode &&
        !!onSelectAllMatching &&
        allOnPageSelected &&
        moreMatchingThanLoaded &&
        !allMatchingSelected;

    const handleSort = (field: SortField) => {
        const nextDir = sortField === field && sortDir === 'asc' ? 'desc' : 'asc';
        onSortChange?.(field, nextDir);
    };

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('nl-NL', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };


    const getCategoryColor = (category?: string) => {
        if (!category) return theme.palette.grey[500];
        switch (category.toLowerCase()) {
            case 'technical': return '#2196f3';
            case 'construction': return '#ff9800';
            case 'transport/logistics': return '#9c27b0';
            case 'facility/cleaning': return '#4caf50';
            case 'administrative': return '#795548';
            default: return theme.palette.grey[500];
        }
    };

    const getOccupation = (w: EasyFlexWorker) => {
        return w.currentOccupation
            || w.currentPlacement?.functionName
            || (w.historicalOccupations?.length > 0 ? w.historicalOccupations[0] : '');
    };

    const getHours = (w: EasyFlexWorker): number => w.totalWorkHours || 0;

    const formatHours = (hours: number) => {
        if (hours >= 1000) return `${(hours / 1000).toFixed(1)}k`;
        return hours.toLocaleString();
    };

    if (loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1">Loading EasyFlex workers...</Typography>
            </Box>
        );
    }

    if (workers.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    No EasyFlex workers found matching your criteria.
                </Typography>
            </Box>
        );
    }

    const headSx = { fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.08), whiteSpace: 'nowrap' as const };

    return (
        <Box>
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <TextField
                    size="small"
                    placeholder="Search all EasyFlex workers..."
                    value={searchValue}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                    sx={{ flex: 1, maxWidth: 480 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchValue ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => onSearchChange?.('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <Typography variant="caption" color="text.secondary">
                    {workers.length} on page • {totalCount} total
                </Typography>
            </Box>

            {showSelectAllMatchingBanner && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '0.875rem',
                    }}
                >
                    <Typography variant="body2">
                        All <strong>{workers.length}</strong> on this page are selected.
                    </Typography>
                    <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={onSelectAllMatching}
                        disabled={isSelectingAllMatching}
                        sx={{ fontWeight: 600 }}
                    >
                        {isSelectingAllMatching
                            ? `Selecting all ${totalCount}…`
                            : `Select all ${totalCount} matching filter`}
                    </Link>
                </Box>
            )}
            {bulkSelectMode && allMatchingSelected && (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        bgcolor: alpha(theme.palette.success.main, 0.08),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography variant="body2">
                        All <strong>{selectedIds?.size ?? 0}</strong> matching workers selected.
                    </Typography>
                </Box>
            )}

            <TableContainer component={Paper} elevation={0}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {bulkSelectMode && (
                                <TableCell
                                    padding="checkbox"
                                    sx={{
                                        ...headSx,
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 3,
                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={allOnPageSelected}
                                        indeterminate={someOnPageSelected}
                                        onChange={(e) =>
                                            onTogglePage?.(workers, e.target.checked)
                                        }
                                    />
                                </TableCell>
                            )}
                            {onImport && (
                                <TableCell
                                    sx={{
                                        ...headSx,
                                        width: 64,
                                        position: 'sticky',
                                        left: bulkSelectMode ? 48 : 0,
                                        zIndex: 3,
                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    }}
                                >
                                    Import
                                </TableCell>
                            )}
                            <TableCell sx={headSx}>
                                <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <PersonIcon fontSize="small" />
                                        <span>Worker</span>
                                    </Stack>
                                </TableSortLabel>
                            </TableCell>
                            {/* Status is a derived value (EasyFlex code + placement), not a
                                real sortable column — slice it with the Availability filter
                                instead. No sort control here so a click can't trigger a
                                backend round-trip that reorders by the stale stored status. */}
                            <TableCell sx={headSx}>Status</TableCell>
                            <TableCell sx={headSx}>
                                <TableSortLabel active={sortField === 'occupation'} direction={sortField === 'occupation' ? sortDir : 'asc'} onClick={() => handleSort('occupation')}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <WorkIcon fontSize="small" />
                                        <span>Occupation</span>
                                    </Stack>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={headSx}>
                                <TableSortLabel active={sortField === 'placement'} direction={sortField === 'placement' ? sortDir : 'asc'} onClick={() => handleSort('placement')}>
                                    Active Placement
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={headSx}>
                                <TableSortLabel active={sortField === 'business'} direction={sortField === 'business' ? sortDir : 'asc'} onClick={() => handleSort('business')}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <BusinessIcon fontSize="small" />
                                        <span>Business</span>
                                    </Stack>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={headSx}>
                                <TableSortLabel active={sortField === 'hours'} direction={sortField === 'hours' ? sortDir : 'asc'} onClick={() => handleSort('hours')}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <AccessTimeIcon fontSize="small" />
                                        <span>Work Hours</span>
                                    </Stack>
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {workers.map((worker) => {
                            const occupation = getOccupation(worker);
                            const totalHours = getHours(worker);
                            const placementCount = worker.placements?.length || 0;

                            const selected = isRowSelected(worker);
                            const imported = importedMatch(worker);
                            const rowClickable = bulkSelectMode ? !!onToggleRow : (!!onImport || !!imported);
                            const handleRowClick = () => {
                                if (bulkSelectMode) onToggleRow?.(worker);
                                else if (imported) onOpenWorker?.(imported.workerId);
                                else if (onImport) onImport(worker);
                            };
                            return (
                                <TableRow
                                    key={worker.id}
                                    hover
                                    selected={selected}
                                    onClick={rowClickable ? handleRowClick : undefined}
                                    sx={{
                                        cursor: rowClickable ? 'pointer' : 'default',
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                                        transition: 'background-color 0.15s',
                                    }}
                                >
                                    {bulkSelectMode && (
                                        <TableCell
                                            padding="checkbox"
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 2,
                                                bgcolor: selected
                                                    ? alpha(theme.palette.primary.main, 0.08)
                                                    : 'background.paper',
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={selected}
                                                onChange={() => onToggleRow?.(worker)}
                                            />
                                        </TableCell>
                                    )}
                                    {onImport && (
                                        <TableCell
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                position: 'sticky',
                                                left: bulkSelectMode ? 48 : 0,
                                                zIndex: 2,
                                                bgcolor: 'background.paper',
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                            }}
                                        >
                                            {imported ? (
                                                <Tooltip title={`Imported${imported.name ? ` as ${imported.name}` : ''} — open worker`}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onOpenWorker?.(imported.workerId)}
                                                        sx={{ color: theme.palette.success.main }}
                                                    >
                                                        <CheckCircleIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Import to recruitment">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => onImport(worker)}
                                                    >
                                                        <PersonAddIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    )}
                                    {/* Worker Info */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    fontSize: '0.8rem',
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    color: theme.palette.primary.main,
                                                }}
                                            >
                                                {worker.firstName?.[0]}{worker.lastName?.[0]}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight="600" noWrap>
                                                    {worker.firstName} {worker.lastName}
                                                </Typography>
                                                <Stack direction="row" spacing={1.5} sx={{ mt: 0.25 }}>
                                                    {worker.email && (
                                                        <Tooltip title={worker.email}>
                                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                                                                <EmailIcon sx={{ fontSize: 13 }} color="action" />
                                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 140 }}>
                                                                    {worker.email}
                                                                </Typography>
                                                            </Stack>
                                                        </Tooltip>
                                                    )}
                                                    {worker.phone && (
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <PhoneIcon sx={{ fontSize: 13 }} color="action" />
                                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                                {worker.phone}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Box>
                                    </TableCell>

                                    {/* Status — granular EasyFlex status + info tooltip */}
                                    <TableCell>
                                        {(() => {
                                            const s = statusBadge(worker);
                                            return (
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Chip
                                                        label={s.label}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: alpha(s.color, 0.12),
                                                            color: s.color,
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            height: 24,
                                                        }}
                                                    />
                                                    <Tooltip title={s.description} arrow>
                                                        <InfoOutlinedIcon
                                                            sx={{ fontSize: 15, color: 'action.active', cursor: 'help' }}
                                                        />
                                                    </Tooltip>
                                                </Stack>
                                            );
                                        })()}
                                    </TableCell>

                                    {/* Occupation */}
                                    <TableCell>
                                        {occupation ? (
                                            <Box>
                                                <Typography variant="body2" fontWeight="500" noWrap sx={{ maxWidth: 200 }}>
                                                    {occupation}
                                                </Typography>
                                                {worker.currentOccupationTranslated && (
                                                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                                                        {worker.currentOccupationTranslated.optimaProfessionName && (
                                                            <Chip
                                                                label={worker.currentOccupationTranslated.optimaProfessionName}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                                            />
                                                        )}
                                                        {worker.currentOccupationTranslated.category && (
                                                            <Chip
                                                                label={worker.currentOccupationTranslated.category}
                                                                size="small"
                                                                sx={{
                                                                    fontSize: '0.65rem',
                                                                    height: 18,
                                                                    bgcolor: alpha(getCategoryColor(worker.currentOccupationTranslated.category), 0.1),
                                                                    color: getCategoryColor(worker.currentOccupationTranslated.category),
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                )}
                                                {worker.historicalOccupations?.length > 1 && (
                                                    <Tooltip title={`Previous: ${worker.historicalOccupations.slice(1).join(', ')}`}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                            +{worker.historicalOccupations.length - 1} previous
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>

                                    {/* Active Placement */}
                                    <TableCell>
                                        {worker.hasActivePlacement && worker.currentPlacement ? (
                                            <Box>
                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                                                    <Chip label="Active" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    {worker.currentPlacement.easyflexPlacementNumber && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            #{worker.currentPlacement.easyflexPlacementNumber}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                                {/* Show relatie/client name prominently */}
                                                {worker.currentPlacement.clientName && (
                                                    <Typography variant="body2" fontWeight="600" noWrap sx={{ maxWidth: 180 }}>
                                                        {worker.currentPlacement.clientName}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 180 }}>
                                                    {worker.currentPlacement.organizationName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {worker.currentPlacement.functionName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {formatDate(worker.currentPlacement.startDate)}
                                                    {worker.currentPlacement.endDate ? ` — ${formatDate(worker.currentPlacement.endDate)}` : ' — heden'}
                                                </Typography>
                                                {worker.currentPlacement.hoursPerWeek && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {worker.currentPlacement.hoursPerWeek}h/week
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Chip label="Available" size="small" variant="outlined" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                {placementCount > 0 && (
                                                    <Tooltip title={`${placementCount} historical placement(s)`}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                            {placementCount} past placement{placementCount !== 1 ? 's' : ''}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        )}
                                    </TableCell>

                                    {/* Business */}
                                    <TableCell>
                                        {worker.business ? (
                                            <Box>
                                                <Typography variant="body2" fontWeight="500" noWrap>
                                                    {worker.business.name}
                                                </Typography>
                                                <Chip
                                                    label={worker.business.businessType.replace(/_/g, ' ').toLowerCase()}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.65rem', height: 18, textTransform: 'capitalize', mt: 0.25 }}
                                                />
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>

                                    {/* Work Hours (total across all placements & orgs) */}
                                    <TableCell>
                                        <Box>
                                            {totalHours > 0 ? (
                                                <>
                                                    <Typography variant="body2" fontWeight="600">
                                                        {formatHours(totalHours)}h
                                                    </Typography>
                                                    {placementCount > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                            {placementCount} placement{placementCount !== 1 ? 's' : ''}
                                                        </Typography>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                            )}
                                        </Box>
                                    </TableCell>

                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalCount > 0 && onPageChange && onRowsPerPageChange && (
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={currentPage}
                    onPageChange={(_, newPage) => onPageChange(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Per page:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} of ${count !== -1 ? count : `${to}+`}`
                    }
                />
            )}
        </Box>
    );
};

export default EasyFlexWorkersTable;
